// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { writable } from 'svelte/store';
import { getApiUrl, isKeycloakEnabled } from '../config.js';
import { i18n } from '../i18n.js';
import { get } from './CoreService';
import { isAuthenticated as authStore, userInfo } from './KeycloakService';

const keycloakIsDisabled = !isKeycloakEnabled() || false;

// store used for activation/deactivation of UI Features
const featureStore = !keycloakIsDisabled
  ? // If Keycloak is enabled features are initially disabled and then set according to user roles from Keycloak
    writable({
      chat: true,
      recherche: false,
      askTheText: false,
      summary: false,
      transcription: false,
      feedback: false,
      tools: {
        onlineSearch: false,
        skills: false,
      },
    })
  : // If Keycloak is disabled, most features are enabled by default
    writable({
      chat: true,
      recherche: true,
      askTheText: false,
      summary: true,
      transcription: true,
      feedback: true,
      tools: {
        onlineSearch: true,
        skills: false,
      },
    });

userInfo.subscribe((user) => {
  if (!user) return;

  const roles = user.roles || [];

  // set feature access based on user roles, if Keycloak is enabled or keep current settings if disabled
  featureStore.update((features) => ({
    ...features,
    chat: keycloakIsDisabled
      ? features.chat
      : roles.indexOf('chat-access') !== -1,
    recherche: keycloakIsDisabled
      ? features.recherche
      : roles.indexOf('rag-database-access') !== -1,
    askTheText: keycloakIsDisabled
      ? features.askTheText
      : roles.indexOf('ask') !== -1,
    summary: keycloakIsDisabled
      ? features.summary
      : roles.indexOf('summary-access') !== -1,
    transcription: keycloakIsDisabled
      ? features.transcription
      : roles.indexOf('transcription-access') !== -1,
    feedback: keycloakIsDisabled
      ? features.feedback
      : roles.indexOf('feedback-access') !== -1,
    tools: {
      onlineSearch: keycloakIsDisabled
        ? features.tools.onlineSearch
        : roles.indexOf('chat-tools-websearch-access') !== -1,
      skills: keycloakIsDisabled
        ? features.tools.skills
        : roles.indexOf('chat-tools-skills-access') !== -1,
    },
  }));
});

// store used for recherche page
const rechercheStore = writable({
  sources: [],
  _currentlySelectedDBSources: [],
  answers: [],
  currentQuestion: '',
  llm: {},
  llm_selected_by_user: false,
});

// store used for 'onyl documents'-settings in recherche page
const documentStore = writable({
  sources: [],
  _currentlySelectedDBSources: [],
  answers: [],
  currentQuestion: '',
  llm: {},
  llm_selected_by_user: false,
});

// store used for summary page
const summaryStore = writable({
  result: null,
  file: null,
  text: null,
  rating: 0,
});

// seperated from recherche store -> able to reacte only to this property
const active_llms_recherche = writable({
  recherche: [],
});

const availableSourcesStore = writable({
  available_sources: [],
});

// store used for chat page
const chatStore = writable({
  messages: [],
  newMessage: '',
  lastUserMessage: '',
  llm: {},
  llm_selected_by_user: false,
});

// seperated from recherche store -> able to reacte only to this property
const active_llms_chat = writable({
  chat: [],
});

const releaseStore = writable({
  releases: [],
  currentVersion: null,
  currentNotes: null,
  currentReleaseDate: null,
});

const textStore = writable({
  texts: null,
});

/**
 * A function to load texts from i18n into our textStore
 */
function loadUITextsFromI18n() {
  const currentTexts = i18n.getResourceBundle(i18n.language, 'translation');
  textStore.update(() => ({
    texts: currentTexts,
  }));
}

loadUITextsFromI18n();

i18n.on('languageChanged', () => {
  loadUITextsFromI18n();
});

/**
 Load Current Version and release notes
 */
function loadReleaseDetails() {
  get('releaseDetails.json')
    .then((r) => r.json())
    .then((data) => {
      releaseStore.update(() => ({
        releases: data.releases,
        currentVersion: data.releases[0].version,
        currentNotes: data.releases[0].releaseNotes,
        currentReleaseDate: data.releases[0].releaseDate,
      }));
    });
}

/**
 Init LLM piplines for chat and get availalbe LLMs
 */

async function get_llms_chat(endpoint = '/chat/llms') {
  try {
    active_llms_chat.update(() => ({
      chat: [
        {
          label: 'Llama-3.3-70B-Instruct',
          is_remote: true,
          name: 'external_ai_model',
        },
      ],
    }));
  } catch (error) {
    console.error('Failed to fetch LLMs for chat:', error);
  }
}

/*
 *
 * Load available LLMs from server.
 */

async function loadAvailableSources() {
  const url = `${getApiUrl()}/rag/sources`;
  try {
    const res = await get(url);

    if (!res.ok) {
      const detail = await res.text().catch(() => null);
      throw new Error(detail || `HTTP ${res.status}`);
    }

    const payload = await res.json();

    const sources = Array.isArray(payload) ? payload : (payload?.sources ?? []);

    availableSourcesStore.update(() => ({
      available_sources: sources,
    }));
  } catch (err) {
    console.debug('Failed to fetch sources', err);
  }
}

// load release Notes from json file
loadReleaseDetails();

// load ui texts from json
loadUITextsFromI18n();

// Subscribe to authentication state and load chat LLMs only when authenticated
// Variable to store the interval ID
let chatLlmsInterval = null;

// Subscribe to the authentication state
authStore.subscribe((isAuth) => {
  if (isAuth || keycloakIsDisabled) {
    // User is authenticated, call all LLM and source loading functions
    get_llms_chat();
    loadAvailableSources();
  } else {
    // User is not authenticated, clear the interval
    if (chatLlmsInterval) {
      clearInterval(chatLlmsInterval);
      chatLlmsInterval = null;
    }
  }
});

// (re-)load active/available LLMs for recherche every 5 minutes

export default {
  rechercheStore: rechercheStore,
  documentStore: documentStore,
  availableSourcesStore: availableSourcesStore,
  chatStore: chatStore,
  active_llms_recherche: active_llms_recherche,
  active_llms_chat: active_llms_chat,
  releaseStore: releaseStore,
  textStore: textStore,
  featureStore: featureStore,
  summaryStore: summaryStore,
};
